import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Package, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { getConsumables } from '@/pages/parametrizacion/consumables/Services/consumables.services';
import { registrarEntradaInsumos } from '@/services/movimientosInsumos.service';

const RegistrarEntradaInsumosModal = ({ isOpen, onClose, onSuccess, user }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [consumibles, setConsumibles] = useState([]);
  const [isLoadingConsumibles, setIsLoadingConsumibles] = useState(false);
  
  const [formData, setFormData] = useState({
    numero_factura: '',
    consecutivo: '',
    observaciones: '',
    insumos: []
  });

  const [newInsumo, setNewInsumo] = useState({
    consumible_id: '',
    cantidad: 1
  });

  // Cargar consumibles
  useEffect(() => {
    if (isOpen) {
      loadConsumibles();
      resetForm();
    }
  }, [isOpen]);

  const loadConsumibles = async () => {
    setIsLoadingConsumibles(true);
    try {
      const response = await getConsumables({
        page: 1,
        limit: 1000,
        query: {}
      });
      setConsumibles(response.data || []);
    } catch (error) {
      console.error('Error al cargar consumibles:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los consumibles',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingConsumibles(false);
    }
  };

  const resetForm = () => {
    setFormData({
      numero_factura: '',
      consecutivo: '',
      observaciones: '',
      insumos: []
    });
    setNewInsumo({
      consumible_id: '',
      cantidad: 1
    });
  };

  const handleAddInsumo = () => {
    if (!newInsumo.consumible_id) {
      toast({
        title: 'Error',
        description: 'Debes seleccionar un consumible',
        variant: 'destructive'
      });
      return;
    }

    if (newInsumo.cantidad <= 0) {
      toast({
        title: 'Error',
        description: 'La cantidad debe ser mayor a 0',
        variant: 'destructive'
      });
      return;
    }

    const consumible = consumibles.find(c => c._id === newInsumo.consumible_id);
    if (!consumible) return;

    // Verificar si ya existe en la lista
    const existingIndex = formData.insumos.findIndex(
      i => i.consumible_id === newInsumo.consumible_id
    );

    if (existingIndex >= 0) {
      // Si existe, sumar la cantidad
      const updatedInsumos = [...formData.insumos];
      updatedInsumos[existingIndex].cantidad += newInsumo.cantidad;
      setFormData({ ...formData, insumos: updatedInsumos });
      
      toast({
        title: 'Cantidad actualizada',
        description: `Se sumaron ${newInsumo.cantidad} unidades a ${consumible.nombre}`,
      });
    } else {
      // Si no existe, agregar nuevo
      setFormData({
        ...formData,
        insumos: [
          ...formData.insumos,
          {
            consumible_id: consumible._id,
            consumible_nombre: consumible.nombre,
            cantidad: newInsumo.cantidad
          }
        ]
      });
    }

    // Reset del formulario de nuevo insumo
    setNewInsumo({
      consumible_id: '',
      cantidad: 1
    });
  };

  const handleRemoveInsumo = (index) => {
    const updatedInsumos = formData.insumos.filter((_, i) => i !== index);
    setFormData({ ...formData, insumos: updatedInsumos });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.insumos.length === 0) {
      toast({
        title: 'Error',
        description: 'Debes agregar al menos un insumo',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.numero_factura && !formData.consecutivo) {
      toast({
        title: 'Error',
        description: 'Debes ingresar el número de factura o consecutivo',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      // Preparar datos para enviar
      const payload = {
        insumos: formData.insumos.map(insumo => ({
          consumible: {
            id: insumo.consumible_id,
            nombre: insumo.consumible_nombre
          },
          cantidad: insumo.cantidad
        })),
        numero_factura: formData.numero_factura,
        consecutivo: formData.consecutivo,
        observaciones: formData.observaciones,
        usuario_id: user?.id,
        usuario_nombre: user?.name
      };

      // Llamar al servicio
      await registrarEntradaInsumos(payload);

      toast({
        title: 'Éxito',
        description: 'Entrada registrada correctamente',
      });

      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error al registrar entrada:', error);
      toast({
        title: 'Error',
        description: 'No se pudo registrar la entrada',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-green-50 border-b border-green-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Registrar Entrada de Insumos
              </h2>
              <p className="text-sm text-gray-600">
                Agrega insumos al inventario con factura o consecutivo
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* Factura y Consecutivo */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="numero_factura">Número de Factura</Label>
                <Input
                  id="numero_factura"
                  value={formData.numero_factura}
                  onChange={(e) => setFormData({ ...formData, numero_factura: e.target.value })}
                  placeholder="FAC-001"
                />
              </div>
              <div>
                <Label htmlFor="consecutivo">Consecutivo</Label>
                <Input
                  id="consecutivo"
                  value={formData.consecutivo}
                  onChange={(e) => setFormData({ ...formData, consecutivo: e.target.value })}
                  placeholder="CONS-001"
                />
              </div>
            </div>

            {/* Agregar Insumo */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Agregar Insumo
              </h3>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-8">
                  <Label htmlFor="consumible">Consumible</Label>
                  <select
                    id="consumible"
                    value={newInsumo.consumible_id}
                    onChange={(e) => setNewInsumo({ ...newInsumo, consumible_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={isLoadingConsumibles}
                  >
                    <option value="">Selecciona un consumible</option>
                    {consumibles.map((consumible) => (
                      <option key={consumible._id} value={consumible._id}>
                        {consumible.nombre} {consumible.descripcion ? `- ${consumible.descripcion}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3">
                  <Label htmlFor="cantidad">Cantidad</Label>
                  <Input
                    id="cantidad"
                    type="number"
                    min="1"
                    value={newInsumo.cantidad}
                    onChange={(e) => setNewInsumo({ ...newInsumo, cantidad: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="col-span-1 flex items-end">
                  <Button
                    type="button"
                    onClick={handleAddInsumo}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Lista de Insumos Agregados */}
            {formData.insumos.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Insumos a Ingresar ({formData.insumos.length})</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium">Consumible</th>
                        <th className="px-4 py-2 text-center text-sm font-medium">Cantidad</th>
                        <th className="px-4 py-2 text-center text-sm font-medium w-20">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.insumos.map((insumo, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-4 py-2">{insumo.consumible_nombre}</td>
                          <td className="px-4 py-2 text-center font-semibold">{insumo.cantidad}</td>
                          <td className="px-4 py-2 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveInsumo(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Observaciones */}
            <div>
              <Label htmlFor="observaciones">Observaciones</Label>
              <Textarea
                id="observaciones"
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                placeholder="Información adicional sobre la entrada..."
                rows={3}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || formData.insumos.length === 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {isLoading ? 'Registrando...' : 'Registrar Entrada'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegistrarEntradaInsumosModal;
